<template>
  <div class="pa-6">
    <div class="bd-layout">
      <!-- ── Left: UPI Overview Card ──────────────── -->
      <div class="bd-main">
        <PremiumPage
          title="Bank Details & Cashfree Settings"
          subtitle="Manage your society's bank account and payment gateway settings"
          content-class="pa-6"
        >
          <v-alert
            type="info"
            border="left"
            colored-border
            color="var(--brand)"
            class="mb-6"
            elevation="0"
            style="background-color: var(--surface2) !important; color: var(--tx) !important;"
          >
            Setting up your bank details allows members to pay their bills directly through the mobile app using. Funds will be directly settled to this bank account.
          </v-alert>

          <v-form ref="bankForm" v-if="dataLoaded">
            <v-row dense>
              <v-col cols="12" md="6">
                <PremiumInput
                  v-model="bank.bank_account_holder_name"
                  label="Account Holder Name *"
                  placeholder="Enter account holder name"
                  icon="mdi-card-account-details-outline"
                  v-validate="'required'"
                  name="bank_account_holder_name"
                  data-vv-scope="bank"
                  data-vv-as="Account Holder Name"
                  :error="errors.first('bank.bank_account_holder_name')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <PremiumInput
                  v-model="bank.bank_account_number"
                  label="Account Number"
                  placeholder="Enter bank account number *"
                  icon="mdi-bank-outline"
                  v-validate="'required|numeric'"
                  name="bank_account_number"
                  data-vv-scope="bank"
                  data-vv-as="Account Number"
                  :error="errors.first('bank.bank_account_number')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <PremiumInput
                  v-model="bank.bank_ifsc"
                  label="IFSC Code *"
                  placeholder="Enter 11-character IFSC code"
                  icon="mdi-barcode"
                  v-validate="'required|length:11'"
                  name="bank_ifsc"
                  data-vv-scope="bank"
                  data-vv-as="IFSC Code"
                  :error="errors.first('bank.bank_ifsc')"
                />
              </v-col>
              <v-col cols="12" md="4">
                <PremiumInput
                  v-model="bank.kyc_account_type"
                  label="Account Type *"
                  placeholder="e.g., Business Entity or Individual"
                  icon="mdi-briefcase-outline"
                  v-validate="'required'"
                  name="kyc_account_type"
                  data-vv-scope="bank"
                  data-vv-as="Account Type"
                  :error="errors.first('bank.kyc_account_type')"
                />
              </v-col>
             
              <v-col cols="12" md="4">
                <PremiumInput
                  v-model="bank.kyc_pan"
                  label="PAN Number *"
                  placeholder="Enter 10-character PAN"
                  icon="mdi-card-account-details-outline"
                  v-validate="'required|length:10'"
                  name="kyc_pan"
                  data-vv-scope="bank"
                  data-vv-as="PAN Number"
                  :error="errors.first('bank.kyc_pan')"
                />
              </v-col>
            </v-row>
          </v-form>

          <div v-if="bank.vendor_status" class="mt-4">
            <div class="d-flex align-center flex-wrap" style="gap:8px">
              <v-chip
                :color="statusChipColor"
                text-color="white"
                small
                class="font-weight-bold"
              >
                <v-icon left x-small>{{ statusChipIcon }}</v-icon>
                {{ statusLabel }}
              </v-chip>
              <span class="caption text-muted" v-if="bank.cashfree_vendor_id">
                Vendor ID: {{ bank.cashfree_vendor_id }}
              </span>
            </div>

            <!-- Rejection Reason Alert -->
            <v-alert
              v-if="bank.vendor_status === 'rejected' && bank.cf_rejection_reason"
              type="error"
              border="left"
              colored-border
              elevation="0"
              dense
              class="mt-3 mb-0"
              style="background: var(--surface2) !important;"
            >
              <div class="font-weight-bold mb-1">Rejection Reason</div>
              <div>{{ bank.cf_rejection_reason }}</div>
              <div class="mt-2 caption" style="opacity:0.8">Please update your bank details and resubmit for verification.</div>
            </v-alert>
          </div>

        <template #footer>
          <v-btn
            depressed
            class="btn-premium-primary"
            @click="saveBankDetails"
            :loading="loading"
            :disabled="bank.vendor_status === 'pending'"
          >
            {{ bank.vendor_status === 'pending' ? 'Verification Pending...' : 'Save Details' }}
          </v-btn>
        </template>
        </PremiumPage>
      </div>

      <!-- ── Right: UPI Overview Card ──────────────── -->
      <div class="bd-sidebar">
        <div class="overview-card" :class="isDark ? 'overview-dark' : 'overview-light'">

          <!-- Card Header -->
          <div class="ov-header">
            <div class="ov-icon-wrap">
              <v-icon color="white" size="20">mdi-lightning-bolt</v-icon>
            </div>
            <div class="ov-header-text">
              <span class="ov-title">UPI Payment Overview</span>
              <span class="ov-subtitle">How the payment flow works</span>
            </div>
          </div>

          <!-- Steps -->
          <div class="ov-steps">
            <!-- Step 1 -->
            <div class="ov-step">
              <div class="ov-step-indicator">
                <div class="ov-step-line top-line"></div>
                <div class="ov-step-dot step-1">
                  <v-icon size="12" color="white">mdi-cog-outline</v-icon>
                </div>
                <div class="ov-step-line"></div>
              </div>
              <div class="ov-step-body">
                <span class="ov-step-label">Admin Setup</span>
                <span class="ov-step-desc">Society admin configures UPI ID in the system. A QR code is auto-generated with the bill amount.</span>
              </div>
            </div>

            <!-- Step 2 -->
            <div class="ov-step">
              <div class="ov-step-indicator">
                <div class="ov-step-line"></div>
                <div class="ov-step-dot step-2">
                  <v-icon size="12" color="white">mdi-qrcode-scan</v-icon>
                </div>
                <div class="ov-step-line"></div>
              </div>
              <div class="ov-step-body">
                <span class="ov-step-label">Member Scans & Pays</span>
                <span class="ov-step-desc">Member scans the QR code with any UPI app (GPay, PhonePe, Paytm) and completes payment.</span>
              </div>
            </div>

            <!-- Step 3 -->
            <div class="ov-step">
              <div class="ov-step-indicator">
                <div class="ov-step-line"></div>
                <div class="ov-step-dot step-3">
                  <v-icon size="12" color="white">mdi-image-plus-outline</v-icon>
                </div>
                <div class="ov-step-line"></div>
              </div>
              <div class="ov-step-body">
                <span class="ov-step-label">Submit Request</span>
                <span class="ov-step-desc">Member uploads a payment screenshot and submits a paid approval request.</span>
              </div>
            </div>

            <!-- Step 4 -->
            <div class="ov-step last-step">
              <div class="ov-step-indicator">
                <div class="ov-step-line"></div>
                <div class="ov-step-dot step-4">
                  <v-icon size="12" color="white">mdi-check-circle-outline</v-icon>
                </div>
                <div class="ov-step-line bottom-line"></div>
              </div>
              <div class="ov-step-body">
                <span class="ov-step-label">Admin Approves</span>
                <span class="ov-step-desc">Admin reviews the screenshot and approves. The bill is automatically marked as paid.</span>
              </div>
            </div>
          </div>

          <!-- Bank Holder Name Input -->

          <div class="ov-upi-section">
               <PremiumInput
                  v-model="bank.bank_account_holder_name"
                  label="Account Holder Name *"
                  placeholder="Enter account holder name"
                  icon="mdi-card-account-details-outline"
                  class="ov-upi-input"
                  :error="upiErrors.bank_account_holder_name"
                  @input="upiErrors.bank_account_holder_name = ''"
                />
          </div>

          <!-- UPI ID Input -->
          <div class="ov-upi-section">
                <PremiumInput
                   v-model="bank.upi_id"
                  label="Your Society UPI ID *"
                   class="ov-upi-input"
                placeholder="e.g. society@okicici"
                  icon="mdi-qrcode"
                v-validate="{ regex: /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/ }"
                data-vv-as="UPI ID"
                name="upi_id"
                data-vv-scope="upi"
                :error="upiErrors.upi_id || errors.first('upi.upi_id')"
                @input="upiErrors.upi_id = ''"
                />
          </div>

          <!-- Supported Apps -->
          <div class="ov-apps">
            <span class="ov-apps-label">Supported UPI Apps</span>
            <div class="ov-apps-chips">
              <span class="app-chip chip-gpay">
                <v-icon size="12" class="mr-1">mdi-google</v-icon>GPay
              </span>
              <span class="app-chip chip-phonepe">
                <v-icon size="12" class="mr-1">mdi-phone</v-icon>PhonePe
              </span>
              <span class="app-chip chip-paytm">
                <v-icon size="12" class="mr-1">mdi-wallet-outline</v-icon>Paytm
              </span>
              <span class="app-chip chip-other">
                <v-icon size="12" class="mr-1">mdi-dots-horizontal</v-icon>Any UPI
              </span>
            </div>
          </div>

          <!-- Info Note -->
          <div class="ov-note">
            <v-icon size="14" color="var(--brand)" class="mr-1">mdi-information-outline</v-icon>
            <span>All payments require manual approval before the bill is marked as paid.</span>
          </div>

          <!-- UPI Save Button -->
          <div class="ov-footer">
            <v-btn
              depressed
              class="btn-premium-primary"
              @click="saveUpiDetails"
              :loading="upiLoading"
            >
              Save UPI Settings
            </v-btn>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import PremiumInput from "../../common/PremiumInput.vue";
import PremiumPage from "../../common/PremiumPage.vue";

export default {
  name: "SocietyAdminBankDetails",

  components: { PremiumInput, PremiumPage },

  data() {
    return {
      loading: false,
      upiLoading: false,
      dataLoaded: false,
      upiErrors: {
        bank_account_holder_name: '',
        upi_id: '',
      },
      bank: {
        bank_account_holder_name: "",
        bank_account_number: "",
        bank_ifsc: "",
        kyc_account_type: "",
        kyc_pan: "",
        cashfree_vendor_id: "",
        vendor_status: "",
        cf_rejection_reason: null,
        upi_id: ""
      }
    };
  },

  computed: {
    ...mapState('theme', ['isDark']),

    statusChipColor() {
      const map = {
        active:      'var(--brand)',
        rejected:    '#ef4444',
        kyc_pending: 'orange',
        pending:     'grey',
      }
      return map[this.bank.vendor_status] || 'grey'
    },

    statusChipIcon() {
      const map = {
        active:      'mdi-check-circle-outline',
        rejected:    'mdi-close-circle-outline',
        kyc_pending: 'mdi-clock-outline',
        pending:     'mdi-timer-sand',
      }
      return map[this.bank.vendor_status] || 'mdi-help-circle-outline'
    },

    statusLabel() {
      const map = {
        active:      'Verified ✓',
        rejected:    'Rejected',
        kyc_pending: 'KYC Pending',
        pending:     'Pending Verification',
      }
      return map[this.bank.vendor_status] || this.bank.vendor_status.toUpperCase()
    },
  },

  created() {
    this.getBankDetails();
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    getBankDetails() {
      let _self = this;
      _self.$axios.get("/societyadmin/bank-details").then((response) => {
        let data = response.data;
        if (data.status && data.data) {
          _self.bank = data.data;
        }
        _self.dataLoaded = true;
      }).catch(() => {
        _self.dataLoaded = true;
      });
    },

    saveUpiDetails() {
      const _self = this;

      // Reset errors
      _self.upiErrors.bank_account_holder_name = '';
      _self.upiErrors.upi_id = '';

      let hasError = false;

      if (!_self.bank.bank_account_holder_name || !_self.bank.bank_account_holder_name.trim()) {
        _self.upiErrors.bank_account_holder_name = 'Account Holder Name is required.';
        hasError = true;
      }

      if (!_self.bank.upi_id || !_self.bank.upi_id.trim()) {
        _self.upiErrors.upi_id = 'UPI ID is required.';
        hasError = true;
      }

      if (hasError) return;

      // validate UPI ID format via vee-validate
      _self.$validator.validate('upi.upi_id', _self.bank.upi_id).then((valid) => {
        if (!valid) return;

        _self.upiLoading = true;
        _self.$axios.post("/societyadmin/bank-details", {
          upi_id: _self.bank.upi_id.trim(),
          bank_account_holder_name: _self.bank.bank_account_holder_name.trim(),
          bank_beneficiary_name: _self.bank.bank_beneficiary_name || ''
        }).then((response) => {
          _self.upiLoading = false;
          const data = response.data;
          if (data.status) {
            _self.setAlert({ showAlert: true, message: 'UPI settings saved successfully.', variant: 'green accent-4', timeout: 3000 });
          } else {
            _self.setAlert({ showAlert: true, message: data.message, variant: 'red accent-3', timeout: 3000 });
          }
        }).catch((error) => {
          _self.upiLoading = false;
          _self.setAlert({ showAlert: true, message: error.response?.data?.message || 'Failed to save UPI settings.', variant: 'red accent-3', timeout: 3000 });
        });
      });
    },

    saveBankDetails() {
      let _self = this;
      // Only validate bank form fields (exclude upi scope)
      this.$validator.validateAll('bank').then((result) => {
        if (result) {
          _self.loading = true;

          _self.$axios
            .post("/societyadmin/bank-details", {
              bank_account_holder_name: _self.bank.bank_account_holder_name,
              bank_account_number: _self.bank.bank_account_number,
              bank_ifsc: _self.bank.bank_ifsc,
              kyc_account_type: _self.bank.kyc_account_type,
              kyc_pan: _self.bank.kyc_pan,
              upi_id: _self.bank.upi_id
            })
            .then((response) => {
              let data = response.data;
              _self.loading = false;

              if (data.status) {
                _self.bank = data.data;
                _self.setAlert({
                  showAlert: true,
                  message: data.message,
                  variant: "green accent-4",
                  timeout: 3000,
                });
              } else {
                _self.setAlert({
                  showAlert: true,
                  message: data.message,
                  variant: "red accent-3",
                  timeout: 3000,
                });
              }
            })
            .catch((error) => {
              _self.loading = false;
              if (error.response?.data?.data) {
                _self.bank = error.response.data.data;
              }
              _self.setAlert({
                showAlert: true,
                message: error.response?.data?.message || 'Failed to save bank details.',
                variant: "red accent-3",
                timeout: 3000,
              });
            });
        }
      });
    }
  }
};
</script>

<style scoped>
/* ── Layout ─────────────────────────────── */
.bd-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: stretch;
}

.bd-sidebar {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* Force PremiumPage to stretch full height */
.bd-main ::v-deep .pp-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.bd-main ::v-deep .pp-content {
  flex: 1;
}

.bd-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  order: 2;
}

.bd-sidebar .overview-card {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.bd-sidebar {
  order: 1;
}

/* ── UPI Section Label ───────────────────── */
.upi-section-label {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--brand, #F87676);
  margin-bottom: 4px;
  padding-bottom: 6px;
  border-bottom: 1px dashed var(--border, #E2E8F0);
}

/* ── Overview Card ───────────────────────── */
.overview-card {
  border-radius: 16px;
  overflow: hidden;
  transition: background 0.2s, border-color 0.2s;
}

.overview-light {
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #E2E8F0);
  box-shadow: 0 1px 3px rgba(0,0,0,0.07), 0 2px 8px rgba(0,0,0,0.04);
}

.overview-dark {
  background: #1F1F1F;
  border: 1px solid rgba(255,255,255,0.1);
  box-shadow: 0 4px 16px rgba(0,0,0,0.35);
}

/* ── Header ─────────────────────────────── */
.ov-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, var(--brand, #F87676) 0%, var(--brand, #F87676) 100%);
  box-shadow: 0 4px 16px rgba(13, 153, 114, 0.4);
}

.ov-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ov-header-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ov-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.ov-subtitle {
  font-size: 13px;
  color: rgba(255,255,255,0.75);
}

/* ── Steps ──────────────────────────────── */
.ov-steps {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.ov-step {
  display: flex;
  gap: 12px;
}

.ov-step-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 28px;
}

.ov-step-line {
  width: 2px;
  flex: 1;
  min-height: 8px;
  background: var(--border, #E2E8F0);
}

.top-line {
  background: transparent;
}

.bottom-line {
  background: transparent;
}

.ov-step-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.step-1 { background: var(--brand, #F87676); }
.step-2 { background: var(--brand, #F87676); }
.step-3 { background: var(--brand, #F87676); }
.step-4 { background: var(--brand, #F87676); }

.ov-step-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 4px 0 16px 0;
}

.last-step .ov-step-body {
  padding-bottom: 4px;
}

.ov-step-label {
  font-size: 14px;
  font-weight: 700;
  color: var(--tx, #111827);
  line-height: 1.3;
}

.ov-step-desc {
  font-size: 12.5px;
  color: var(--tx3, #9CA3AF);
  line-height: 1.5;
}

/* ── Supported Apps ─────────────────────── */
.ov-apps {
  padding: 12px 16px;
  border-top: 1px solid var(--border, #E2E8F0);
}

.ov-apps-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--tx3, #9CA3AF);
  margin-bottom: 8px;
}

.ov-apps-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.app-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid transparent;
}

.chip-gpay    { background: rgba(13, 153, 114, 0.08); color: var(--brand, #F87676); border-color:var(--brand, #F87676); }
.chip-phonepe { background: rgba(13, 153, 114, 0.08); color: var(--brand, #F87676); border-color:var(--brand, #F87676);}
.chip-paytm   { background: rgba(13, 153, 114, 0.08); color: var(--brand, #F87676); border-color:var(--brand, #F87676);}
.chip-other   { background: var(--surface2, #f3f4f6); color: var(--tx3, #6b7280); border-color: var(--border, #e5e7eb); }

/* ── UPI Input in Overview Card ───────────── */
.ov-upi-section {
  padding: 12px 16px;
  border-top: 1px solid var(--border, #E2E8F0);
}

.ov-upi-label {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--brand, #F87676);
  margin-bottom: 8px;
}

.ov-upi-input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--surface2, #f8fafc);
  border: 1.5px solid var(--border, #E2E8F0);
  border-radius: 8px;
  padding: 0 10px;
  height: 36px;
  transition: border-color 0.2s;
}

.ov-upi-input-wrap:focus-within {
  border-color: var(--brand, #F87676);
  background: var(--surface, #fff);
}

.ov-upi-icon {
  flex-shrink: 0;
}

.ov-upi-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: var(--tx, #111827);
  min-width: 0;
}

.ov-upi-input::placeholder {
  color: var(--tx3, #9CA3AF);
}

.ov-upi-input-wrap.has-error {
  border-color: #ff5252;
  background: #fffafa;
}

.upi-error-msg {
  color: #ff5252;
  font-size: 11px;
  margin-top: 4px;
  padding-left: 4px;
}

/* ── Note ──────────────────────────────── */
.ov-note {
  display: flex;
  align-items: flex-start;
  padding: 10px 16px 14px;
  font-size: 12.5px;
  color: var(--tx3, #9CA3AF);
  line-height: 1.5;
  border-top: 1px solid var(--border, #E2E8F0);
}

/* ── UPI Card Footer Button ─────────────── */
.ov-footer {
  padding: 12px 16px 16px;
  border-top: 1px solid var(--border, #E2E8F0);
}

/* ── Misc ───────────────────────────────── */
.text-muted {
  color: var(--tx3);
}

/* ── Responsive ──────────────────────────── */
@media (max-width: 960px) {
  .bd-layout {
    grid-template-columns: 1fr;
  }
}

/* ════════════════════════════════════════
   BANK DETAILS — DARK MODE FIXES
   Overview card: header, steps, chips, button
════════════════════════════════════════ */

/* ── Overview Card base ── */
.theme--dark .overview-card.overview-dark {
  background: var(--surface2, #1F1F1F) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35) !important;
}

/* ── Header gradient — keep brand color visible ── */
.theme--dark .ov-header {
  background: linear-gradient(
    135deg,
    var(--brand, #E5E5E5) 0%,
    var(--brand-dk, #D4D4D4) 100%
  ) !important;
  box-shadow: 0 4px 16px var(--brand-glow, rgba(255,255,255,0.12)) !important;
}

.theme--dark .ov-title {
  color: #121212 !important;   /* dark text on light gradient in dark mode */
}

.theme--dark .ov-subtitle {
  color: rgba(0, 0, 0, 0.65) !important;
}

.theme--dark .ov-icon-wrap {
  background: rgba(0, 0, 0, 0.15) !important;
}

.theme--dark .ov-icon-wrap .v-icon {
  color: #121212 !important;
}

/* ── Step connector lines ── */
.theme--dark .ov-step-line {
  background: rgba(255, 255, 255, 0.1) !important;
}

/* ── Step dots — use brand token, keep icon readable ── */
.theme--dark .ov-step-dot {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4) !important;
  /* brand color is already applied via step-1..4 classes using var(--brand) */
}

.theme--dark .ov-step-dot .v-icon {
  color: #121212 !important; /* dark icon on light brand dot */
}

/* ── Step label & description ── */
.theme--dark .ov-step-label {
  color: var(--tx, #FAFAFA) !important;
}

.theme--dark .ov-step-desc {
  color: var(--tx3, #A3A3A3) !important;
}

/* ── Section dividers inside card ── */
.theme--dark .ov-apps,
.theme--dark .ov-upi-section,
.theme--dark .ov-note,
.theme--dark .ov-footer {
  border-top-color: rgba(255, 255, 255, 0.07) !important;
}

/* ── Supported UPI Apps label ── */
.theme--dark .ov-apps-label {
  color: var(--tx4, #737373) !important;
}

/* ── App chips — brand-tinted in dark ── */
.theme--dark .app-chip {
  background: rgba(255, 255, 255, 0.06) !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: var(--tx2, #D4D4D4) !important;
}

.theme--dark .app-chip .v-icon {
  color: inherit !important;
}

/* GPay / PhonePe / Paytm — subtle brand tint */
.theme--dark .chip-gpay,
.theme--dark .chip-phonepe,
.theme--dark .chip-paytm {
  background: var(--brand-lt, rgba(255,255,255,0.06)) !important;
  color: var(--brand, #E5E5E5) !important;
  border-color: var(--brand, #E5E5E5) !important;
  opacity: 0.85;
}

/* Any UPI chip */
.theme--dark .chip-other {
  background: var(--surface3, #262626) !important;
  color: var(--tx3, #A3A3A3) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

/* ── Info note ── */
.theme--dark .ov-note {
  color: var(--tx3, #A3A3A3) !important;
}

.theme--dark .ov-note .v-icon {
  color: var(--brand, #E5E5E5) !important;
}

/* ── UPI section labels ── */
.theme--dark .ov-upi-label {
  color: var(--brand, #E5E5E5) !important;
}

/* ── Save UPI Settings button ── */
.theme--dark .ov-footer .btn-premium-primary {
  background: var(--brand, #E5E5E5) !important;
  color: #121212 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
}

.theme--dark .ov-footer .btn-premium-primary:hover {
  filter: brightness(1.08) !important;
  transform: translateY(-1px);
}

/* ── Vendor status chip in dark ── */
.theme--dark .v-chip[color="var(--brand)"] {
  color: #121212 !important;
}

/* ── Rejection alert in dark ── */
.theme--dark .v-alert[type="error"] {
  background: rgba(239, 68, 68, 0.08) !important;
  border-left-color: #ef4444 !important;
}

/* ── Info alert at top ── */
.theme--dark .v-alert[type="info"] {
  background: var(--surface2, #1F1F1F) !important;
  border-left-color: var(--brand, #E5E5E5) !important;
  color: var(--tx2, #D4D4D4) !important;
}

</style>
